import { useTranslation } from 'react-i18next';

import { imgOnError } from '../../../../../javascript/shared/helpers';
import { useReportsFeedbackPaginated, useReportsFeedbackAll } from '../data';
import { useTileStatusTabs } from '../../hooks/useTileStatusTabs';
import TdWithTooltip from './TdWithTooltip.jsx';
import Sort from '../../../../shared/Sort';
import {
  ThContentWrapper,
  ColumnTitle,
  Th,
  Td,
  TableIcon,
  Table,
  Tr,
  ShadedTr,
} from '../../../../shared/TableComponents/StyledComponents';
import Card from '../../../../shared/Card';
import Placeholder from '../../../../shared/Placeholder';
import Tooltip from '../../../../shared/Tooltip';

const FeedbackTable = ({ data, sortingStatus, changeSort }) => {
  const { t } = useTranslation('reports');

  return (
    <>
      <Tooltip id="feedback-header" />
      <Tooltip id="Feedback" />

      <Table>
        <colgroup>
          <col span="1" style={{ width: '72px' }}></col>
          <col span="1" style={{ width: 'calc(35% - 154px)' }}></col>
          <col span="1" style={{ width: '82px' }}></col>
          <col span="1" style={{ width: '15%' }}></col>
          <col span="1" style={{ width: '50%' }}></col>
        </colgroup>
        <thead>
          <Tr>
            <Th
              data-tip={t('campaigns.tileTooltip')}
              data-for="feedback-header"
            >
              Tile
            </Th>
            <Th>
              <ThContentWrapper onClick={() => changeSort('headline')}>
                <Sort sortingStatus={sortingStatus} columnName="headline" />
                <ColumnTitle
                  data-tip={t('campaigns.headlineTooltip')}
                  data-for="feedback-header"
                >
                  Headline
                </ColumnTitle>
              </ThContentWrapper>
            </Th>
            <Th>
              <ThContentWrapper onClick={() => changeSort('created_at')}>
                <Sort sortingStatus={sortingStatus} columnName="created_at" />
                <ColumnTitle
                  data-tip={t('campaigns.dateTooltip')}
                  data-for="feedback-header"
                >
                  Date
                </ColumnTitle>
              </ThContentWrapper>
            </Th>
            <Th>
              <ThContentWrapper onClick={() => changeSort('user_name')}>
                <Sort sortingStatus={sortingStatus} columnName="user_name" />
                <ColumnTitle
                  data-tip={t('campaigns.nameTooltip')}
                  data-for="feedback-header"
                >
                  Name
                </ColumnTitle>
              </ThContentWrapper>
            </Th>
            <Th>
              <span
                data-tip={t('campaigns.feedbackTooltip')}
                data-for="feedback-header"
              >
                Feedback
              </span>
            </Th>
          </Tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <ShadedTr
              key={i}
              className={`stat_action js-open-tile-stats-modal`}
              data-tile-id={row.tileId}
              data-href={`/client_admin/tiles/${row.tileId}/tile_stats`}
              data-tab="Reports tab"
              data-sub-tab="Survey Tiles"
              style={{ cursor: 'pointer' }}
            >
              <Td textAlign="left">
                <TableIcon src={row.tileImage} onError={imgOnError} />
              </Td>
              <TdWithTooltip
                className="tile-headline"
                dataFor="Feedback"
                content={row.tileHeadline}
                as="div"
                color="primary.100"
              />
              <TdWithTooltip
                className="answer-date"
                dataFor="Feedback"
                content={row.date}
                as="div"
              />
              <TdWithTooltip
                className="user-name"
                dataFor="Feedback"
                content={row.userName}
                as="div"
              />
              <TdWithTooltip
                className="tile-feedback"
                dataFor="Feedback"
                content={row.feedback}
                as="div"
              />
            </ShadedTr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

const FeedbackTablePaginated = ({
  onClick,
  sortingStatus,
  changeSort,
  campaignId,
}) => {
  const {
    activeTab,
    tileStatus,
    page,
    tabs,
    setPage,
    handleTabClick,
  } = useTileStatusTabs('feedback');
  const { t } = useTranslation('reports');
  const itemsPerPage = 10;
  const onPageChange = data => setPage(data.selected + 1);
  const {
    isLoading,
    data = { data: [], pagy: {} },
  } = useReportsFeedbackPaginated({
    page,
    itemsPerPage,
    tileStatus,
    campaignId,
    ...sortingStatus,
  });

  const { pagy } = data;

  return (
    <Card
      title={t('campaigns.feedbackTitle')}
      titleTooltip={t('campaigns.feedbackTitleTooltip')}
      items={itemsPerPage}
      onClick={onClick}
      canShowAll={data.data.length > 0}
      isShowingAll={false}
      isLoading={isLoading}
      pagy={data.data.length > 0 ? pagy : null}
      onPageChange={onPageChange}
      className="feedback-section"
      tabs={tabs}
      activeTab={activeTab}
      tabClick={handleTabClick}
      margin="0"
    >
      {data.data.length ? (
        <FeedbackTable
          data={data.data}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
        />
      ) : (
        <Placeholder />
      )}
    </Card>
  );
};

const FeedbackTableAll = ({
  onClick,
  sortingStatus,
  changeSort,
  campaignId,
}) => {
  const { activeTab, tileStatus, tabs, handleTabClick } = useTileStatusTabs(
    'feedback',
  );
  const { t } = useTranslation('reports');
  const { isLoading, data = { data: [] } } = useReportsFeedbackAll({
    campaignId,
    tileStatus,
    ...sortingStatus,
  });

  return (
    <Card
      title={t('campaigns.feedbackTitle')}
      titleTooltip={t('campaigns.feedbackTitleTooltip')}
      onClick={onClick}
      canShowAll={data.data.length > 0}
      isShowingAll={true}
      isLoading={isLoading}
      className="feedback-section"
      tabs={tabs}
      activeTab={activeTab}
      tabClick={handleTabClick}
      margin="0"
    >
      {data.data.length ? (
        <FeedbackTable
          data={data.data}
          sortingStatus={sortingStatus}
          changeSort={changeSort}
        />
      ) : (
        <Placeholder />
      )}
    </Card>
  );
};

export const Feedback = ({
  onClick,
  showAll,
  sortingStatus,
  changeSort,
  campaignId,
}) => {
  return showAll ? (
    <FeedbackTableAll
      onClick={onClick}
      sortingStatus={sortingStatus}
      changeSort={changeSort}
      campaignId={campaignId}
    />
  ) : (
    <FeedbackTablePaginated
      onClick={onClick}
      sortingStatus={sortingStatus}
      changeSort={changeSort}
      campaignId={campaignId}
    />
  );
};
